import type { OwnershipFlags } from '@risksmart-app/trpc/src/routers/frontend/shared/ownership-filter-input';
import type { GetMyDueItemsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import dayjs from 'dayjs';
import { useGetMyDueItems } from 'src/hooks/queries/my-items/useGetMyDueItems';

import type { WidgetQueryParams } from '../../gigawidget/types';
import type { MyOverdueItemFields } from '../../my-items/config';
import { useGetMyOverdueItemsSmartWidgetTableProps } from '../../my-items/config';
import { defaultMyItemsFilter } from '../../useDashboardStore';
import { createDataSource } from '../createDataSource';

/**
 * Only the ownership half is a dashboard filter. The due-by horizon is a
 * per-descriptor constant, bound in the closure below rather than added as a
 * descriptor field (COI-600 §4.4).
 */
type MyOverdueItemsArgs = { ownershipFilter?: OwnershipFlags };

const createMyItemsDataSource = (dueDateLength: number) =>
  createDataSource<MyOverdueItemFields, MyOverdueItemsArgs, GetMyDueItemsQuery>(
    {
      group: 'single',
      parentTypes: [],
      hasAccess: () => true,
      // `endOf('day')` makes the horizon stable within a calendar day, so the
      // react-query key does not churn on every render.
      useWidgetQuery: (params: WidgetQueryParams<MyOverdueItemsArgs>) =>
        useGetMyDueItems({
          ...params,
          queryArgs: {
            date: dayjs().add(dueDateLength, 'day').endOf('day').toISOString(),
            // Reproduces the old `vars.ownershipFilter ?? defaultMyItemsFilter`.
            // `useGetMyDueItems` documents that an all-false flag set yields an
            // empty feed, and an absent one will not typecheck.
            ownershipFilter:
              params.queryArgs.ownershipFilter ?? defaultMyItemsFilter,
          },
        }),
      useTablePropsHook: (data, options) =>
        useGetMyOverdueItemsSmartWidgetTableProps(data, options),
      fields: 'dashboard.myItemsDashboard.fields',
      entityNamePlural: 'my_item_other',
      entityNameSingular: 'my_item_one',
      dashboardFilterConfig: {
        ownershipFilter: (myItemsFilters) => ({
          ownershipFilter: myItemsFilters,
        }),
      },
      categoryGetters: [],
    }
  );

export const myOverdueItems7Days = createMyItemsDataSource(7);
export const myOverdueItems30Days = createMyItemsDataSource(30);
